import { useEffect } from "react";

/**
 * useBfcacheRestore — Android Chrome / iOS Safari restauran la página desde el
 * Back/Forward Cache al volver del historial o desbloquear el teléfono. En ese
 * momento React no vuelve a montar y los listeners de scroll/resize no
 * re-disparan, así que ciertos cálculos dependientes del viewport (viewport
 * dinámico, safe-area, sticky/fixed) quedan desalineados.
 *
 * Antes forzábamos `location.reload()` en cada restauración. Eso descartaba el
 * DOM ya pintado y, en móvil con red lenta, la nueva carga podía mostrarse antes
 * de que el CSS estuviera aplicado (menú como lista de enlaces, botones sin
 * estilo, hero en bloque de color).
 *
 * Ahora sólo recargamos si la página realmente perdió su hoja de estilos; en el
 * caso normal basta con re-disparar los eventos de layout.
 */
function stylesAreApplied(): boolean {
  try {
    const probe = document.createElement("div");
    probe.className = "hidden";
    probe.setAttribute("aria-hidden", "true");
    document.body.appendChild(probe);
    const display = window.getComputedStyle(probe).display;
    probe.remove();
    return display === "none";
  } catch {
    return true;
  }
}

export function useBfcacheRestore() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (!stylesAreApplied()) {
        window.location.reload();
        return;
      }
      window.dispatchEvent(new Event("resize"));
      window.dispatchEvent(new Event("scroll"));
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
}
